import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type { AppState, EntityMap, BoundaryMap } from '@/types';
import { createEntityMap, createBoundaryMap } from '@/lib/indexers';

interface AppContextType extends AppState {
  setAppState: (state: Partial<AppState>) => void;
  resetAppState: () => void;
  setCurrentDiagram: (index: number) => void;
  entitiesMap: EntityMap;
  boundariesMap: BoundaryMap;
  fileName: string | null;
  fileSize: number | null;
  setFileInfo: (name: string, size: number) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  notesEditing: boolean;
  setNotesEditing: (val: boolean) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

const initialState: AppState = {
  isProject: false,
  projectInfo: null,
  diagrams: [],
  threats: [],
  notes: [],
  reviewComments: [],
  handovers: [],
  currentDiagramIndex: 0,
  currentDiagram: null,
};

export function AppProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AppState>(initialState);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [entitiesMap, setEntitiesMap] = useState<EntityMap>({});
  const [boundariesMap, setBoundariesMap] = useState<BoundaryMap>({});
  const [activeTab, setActiveTab] = useState<string>('overview');
  const [notesEditing, setNotesEditing] = useState<boolean>(false);

  const setAppState = useCallback((newState: Partial<AppState>) => {
    setState((prev) => {
      const updated = { ...prev, ...newState };

      // If diagrams array changed, ensure currentDiagramIndex is in range and
      // update currentDiagram to keep them in sync.
      if (newState.diagrams) {
        const diagramsArr = newState.diagrams as typeof prev.diagrams;
        let idx = updated.currentDiagramIndex || 0;
        if (idx < 0) idx = 0;
        if (idx >= diagramsArr.length) idx = diagramsArr.length - 1;
        updated.currentDiagramIndex = idx;
        updated.currentDiagram = diagramsArr[idx] || null;
      }

      // If switching between project/non-project, ensure activeTab has a sensible default
      if (newState.isProject !== undefined) {
        if (newState.isProject) {
          const hasDiagrams = (newState.diagrams ?? prev.diagrams).length > 0;
          setActiveTab(hasDiagrams ? 'diagrams' : 'project-info');
        } else {
          setActiveTab('overview');
        }
      }
      
      // Update maps when current diagram changes
      if (updated.currentDiagram) {
        setEntitiesMap(createEntityMap(updated.currentDiagram));
        setBoundariesMap(createBoundaryMap(updated.currentDiagram));
      }
      
      return updated;
    });
  }, []);

  const resetAppState = useCallback(() => {
    setState(initialState);
    setFileName(null);
    setFileSize(null);
    setEntitiesMap({});
    setBoundariesMap({});
    setActiveTab('overview');
    setNotesEditing(false);
  }, []);

  const setCurrentDiagram = useCallback((index: number) => {
    setState((prev) => {
      if (index < 0 || index >= prev.diagrams.length) return prev;
      
      const newDiagram = prev.diagrams[index];
      setEntitiesMap(createEntityMap(newDiagram));
      setBoundariesMap(createBoundaryMap(newDiagram));
      
      return {
        ...prev,
        currentDiagramIndex: index,
        currentDiagram: newDiagram,
      };
    });
  }, []);

  const setFileInfo = useCallback((name: string, size: number) => {
    setFileName(name);
    setFileSize(size);
  }, []);

  const value: AppContextType = {
    ...state,
    setAppState,
    resetAppState,
    setCurrentDiagram,
    entitiesMap,
    boundariesMap,
    fileName,
    fileSize,
    setFileInfo,
    activeTab,
    setActiveTab,
    notesEditing,
    setNotesEditing,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
}
